<template>
  <div>
    <Menu />

    <div v-if="loading" class="py-5 text-center">
      Loading property...
    </div>

    <div v-else-if="!property">
      <div class="container py-5 text-center">
        <h1>Property not found</h1>
        <p>This property may no longer be available.</p>
        <a href="/properties" class="btn btn-all">Back to Properties</a>
      </div>
    </div>

    <div v-else-if="canDisplayProperty">
      <header class="site-nav">
        <div class="container">
          <div class="site-nav-inner d-flex align-items-center justify-content-between">
            <div class="brand-lockup">
              <a href="/home" class="d-flex align-items-center text-decoration-none">
                <img src="https://www.stratitude.co.za/chas-international/dubai/images/chas-logo.svg" alt="Chas Everitt" class="brand-logo" loading="lazy">
              </a>

              <span class="brand-divider" aria-hidden="true"></span>

              <a href="https://www.leadingre.com/" class="d-flex align-items-center text-decoration-none" target="_blank" rel="noopener noreferrer">
                <img src="https://www.stratitude.co.za/chas-international/dubai/images/leading-logo1.png" alt="Leading Real Estate" class="partner-logo" loading="lazy">
              </a>
            </div>

            <nav class="d-none d-lg-flex site-nav-links align-items-center">
              <a class="nav-link" href="/properties">Buy</a>
              <a class="nav-link" href="/sell">Sell</a>
              <a class="nav-link" href="/developments">Developments</a>
              <a class="nav-link" href="/commercial">Commercial</a>
            </nav>

            <button
              class="btn hamburger d-lg-none"
              type="button"
              data-bs-toggle="offcanvas"
              data-bs-target="#mobileNav"
              aria-label="Open menu"
            >
              <i class="bi bi-list" style="font-size:1.25rem;"></i>
            </button>
          </div>
        </div>
      </header>

      <section class="property-hero d-flex align-items-end">
        <button
          type="button"
          class="hero-image-button"
          @click="openGallery(0)"
          aria-label="Open gallery"
        >
          <img
            :src="featuredImage"
            class="hero-image"
            :alt="property.marketing_heading"
          />
        </button>

        <div class="hero-overlay"></div>

        <div class="container pb-4 hero-content">
          <div class="property-kicker">
            {{ property.listing_type || 'Property' }}
          </div>

          <h1 class="property-title mt-2 text-uppercase">
            <strong>{{ property.marketing_heading }}</strong>
          </h1>

          <div class="property-sub">
            {{ displayPrice }}
            <span v-if="hasConvertedPrice">
              • {{ displayPriceZAR }} / {{ displayPriceUSD }}
            </span>
            <span v-if="property.price_term">• {{ property.price_term }}</span>
            <span v-if="property.loc_suburb || property.loc_region">
              • {{ property.loc_suburb }}, {{ property.loc_region }}
            </span>
          </div>
        </div>
      </section>

      <section class="property-summary">
        <div class="container">
          <div class="d-flex flex-wrap align-items-center gap-2 justify-content-center justify-content-md-between">
            <div class="d-flex flex-wrap gap-2 justify-content-center justify-content-md-start">
              <div class="summary-chip" v-if="showNumber(property.bedrooms)">
                Beds <strong>{{ property.bedrooms }}</strong>
              </div>

              <div class="summary-chip" v-if="showNumber(property.bathrooms)">
                Baths <strong>{{ property.bathrooms }}</strong>
              </div>

              <div class="summary-chip" v-if="showNumber(property.lounges)">
                Lounges <strong>{{ property.lounges }}</strong>
              </div>

              <div class="summary-chip" v-if="parkingCount > 0">
                Parking <strong>{{ parkingCount }}</strong>
              </div>

              <div class="summary-chip" v-if="showNumber(property.building_size)">
                Size <strong>{{ formatSize(property.building_size, property.building_measurement_type) }}</strong>
              </div>
            </div>

            <button
              type="button"
              class="btn btn-gallery ms-md-auto"
              @click="openGallery(0)"
              v-if="galleryImages.length > 0"
            >
              View Gallery
            </button>
          </div>
        </div>
      </section>

      <section class="py-5 bg-white">
        <div class="container">
          <div class="row g-4">
            <div class="col-12 col-lg-8">
              <div class="section-title">About Property</div>
              <div class="section-rule"></div>

              <div class="paragraph property-description" v-html="formattedDescription"></div>

              <div class="regulatory-panel mt-5">
                <div class="row g-4 align-items-start">
                  <div class="col-12 col-xl-4">
                    <h3 class="regulatory-title">Property details</h3>

                    <div class="reg-row">
                      <strong>Property Type</strong>
                      <span>{{ property.property_type || '—' }}</span>
                    </div>

                    <div class="reg-row" v-if="showNumber(property.building_size)">
                      <strong>Property Size</strong>
                      <span>{{ formatSize(property.building_size, property.building_measurement_type) }}</span>
                    </div>

                    <div class="reg-row" v-if="showNumber(property.bedrooms)">
                      <strong>Bedrooms</strong>
                      <span>{{ property.bedrooms }}</span>
                    </div>

                    <div class="reg-row" v-if="showNumber(property.bathrooms)">
                      <strong>Bathrooms</strong>
                      <span>{{ property.bathrooms }}</span>
                    </div>

                    <div class="reg-row" v-if="property.occupation_date">
                      <strong>Available from</strong>
                      <span>{{ formatDate(property.occupation_date) }}</span>
                    </div>
                  </div>

                  <div class="col-12 col-xl-5">
                    <h3 class="regulatory-title">Regulatory information</h3>

                    <div class="reg-row">
                      <strong>Reference</strong>
                      <span>{{ property.web_ref || property.own_ref_number || property.id || '—' }}</span>
                    </div>

                    <div class="reg-row">
                      <strong>Listed</strong>
                      <span>{{ listedAgo }}</span>
                    </div>

                    <div class="reg-row">
                      <strong>Broker License</strong>
                      <span>{{ brokerLicense }}</span>
                    </div>

                    <div class="reg-row">
                      <strong>Agency name</strong>
                      <span>Chas Everitt International</span>
                    </div>

                    <div class="reg-row">
                      <strong>Zone name</strong>
                      <span>{{ property.loc_region || property.loc_suburb || 'Dubai' }}</span>
                    </div>

                    <div class="reg-row">
                      <strong>Agent License</strong>
                      <span>{{ agentLicense }}</span>
                    </div>
                  </div>

                  <div class="col-12 col-xl-3 text-center">
                    <img
                      v-if="dldQrCode"
                      :src="dldQrCode"
                      class="dld-qr"
                      alt="DLD QR Code"
                    />

                    <div class="dld-label mt-3">DLD Permit Number</div>

                    <img
                      v-if="dldPermitImage"
                      :src="dldPermitImage"
                      class="dld-permit-image"
                      alt="DLD Permit Number"
                    />
                  </div>
                </div>
              </div>

              <div class="mt-5" v-if="extrasList.length > 0">
                <div class="section-title">Features</div>
                <div class="section-rule"></div>

                <div class="row g-2">
                  <div class="col-12 col-md-6" v-for="extra in extrasList" :key="extra">
                    <div class="feature-item">
                      <i class="bi bi-check-circle me-2"></i>{{ extra }}
                    </div>
                  </div>
                </div>
              </div>

              <div class="mt-5" v-if="property.video_id">
                <div class="section-title">Video</div>
                <div class="section-rule"></div>

                <div class="property-box p-2">
                  <div class="ratio ratio-16x9">
                    <iframe
                      :src="'https://www.youtube.com/embed/' + property.video_id + '?rel=0&modestbranding=1'"
                      title="Property video"
                      allowfullscreen
                    ></iframe>
                  </div>
                </div>
              </div>

              <div class="mt-5" v-if="hasMap">
                <div class="section-title">Location</div>
                <div class="section-rule"></div>

                <iframe
                  width="100%"
                  height="450"
                  frameborder="0"
                  style="border:0"
                  :src="'/map.html?lat=' + property.map_x_position + '&lng=' + property.map_y_position + '&zoom=12'"
                  allowfullscreen
                ></iframe>
              </div>
            </div>

            <div class="col-12 col-lg-4">
              <div class="property-box">
                <div class="ref-label">Price</div>
                <div class="property-price">
                  <div>{{ displayPrice }}</div>

                  <template v-if="hasConvertedPrice">
                    <div class="converted-property-price">
                      Approx. {{ displayPriceZAR }}
                    </div>
                    <div class="converted-property-price">
                      Approx. {{ displayPriceUSD }}
                    </div>
                  </template>
                </div>

                <div class="ref-label">Reference</div>
                <p class="mb-0 fw-bold">
                  {{ property.web_ref || property.own_ref_number || property.id || '—' }}
                </p>

                <div class="ref-label mt-3" v-if="property.viewing_notes">Viewing Notes</div>
                <p class="mb-0" v-if="property.viewing_notes">{{ property.viewing_notes }}</p>

                <div class="d-grid gap-2 mt-3">
                  <a href="#agents" class="btn btn-all">Contact Agent</a>
                  <button type="button" class="btn btn-outline-brand" @click="printToPDF">
                    Print Property
                  </button>
                </div>
              </div>

              <div id="agents" class="agent-list-card mt-4" v-if="agentsList.length > 0">
                <div class="section-title">Agent Details</div>
                <div class="section-rule"></div>

                <div
                  class="agent-list-item"
                  v-for="agentItem in agentsList"
                  :key="agentItem.key"
                >
                  <div class="agent-list-photo" v-if="agentItem.agent.agent_photo">
                    <img
                      :src="agentItem.agent.agent_photo"
                      :alt="agentItem.agent.first_name + ' ' + agentItem.agent.last_name"
                    >
                  </div>

                  <div class="agent-list-photo agent-list-photo-placeholder" v-else>
                    <i class="bi bi-person"></i>
                  </div>

                  <div class="agent-list-content">
                    <p class="agent-name">
                      {{ agentItem.agent.first_name }} {{ agentItem.agent.last_name }}
                    </p>

                    <p class="agent-role" v-if="agentItem.agent.position || agentItem.agent.role">
                      {{ agentItem.agent.position || agentItem.agent.role }}
                    </p>

                    <div class="agent-contact-list" v-if="agentItem.contactRequested">
                      <a
                        v-if="agentItem.agent.cell_number"
                        :href="'tel:+' + formatPhoneForLink(agentItem.agent.cell_number)"
                      >
                        <i class="bi bi-telephone me-2"></i>{{ formatPhoneForDisplay(agentItem.agent.cell_number) }}
                      </a>

                      <a
                        v-if="agentItem.agent.email_address"
                        :href="'mailto:' + agentItem.agent.email_address"
                      >
                        <i class="bi bi-envelope me-2"></i>{{ agentItem.agent.email_address }}
                      </a>

                      <a
                        v-if="agentItem.whatsappLink"
                        :href="agentItem.whatsappLink"
                        target="_blank"
                        rel="noopener"
                      >
                        <i class="bi bi-whatsapp me-2"></i>WhatsApp
                      </a>
                    </div>

                    <div class="agent-actions mt-3">
                      <button
                        v-if="!agentItem.contactRequested"
                        class="btn btn-all btn-sm"
                        type="button"
                        @click="requestAgentContact(agentItem.key)"
                      >
                        Details
                      </button>

                      <a
                        v-else-if="agentItem.whatsappLink"
                        class="btn btn-all btn-sm"
                        :href="agentItem.whatsappLink"
                        target="_blank"
                        rel="noopener"
                      >
                        Enquire Now
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div class="floating-whatsapp" v-if="agentsList.length > 0">
        <div class="d-flex flex-column align-items-start">
          <div
            class="d-flex align-items-center mb-2"
            v-for="agentItem in agentsList"
            :key="'float-' + agentItem.key"
          >
            <i class="bi bi-whatsapp text-danger me-2"></i>
            <a
              target="_blank"
              class="text-decoration-none text-danger"
              :href="agentItem.whatsappLink"
              v-if="agentItem.whatsappLink"
            >
              <strong>{{ agentItem.agent.first_name }}</strong>
            </a>
          </div>
        </div>
      </div>

      <div class="modal fade" id="galleryModal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-fullscreen modal-dialog-centered">
          <div class="modal-content gallery-modal">
            <div class="modal-header">
              <h5 class="modal-title">
                Gallery
                <span v-if="galleryImages.length > 0">
                  {{ activeGalleryIndex + 1 }} / {{ galleryImages.length }}
                </span>
              </h5>

              <div class="btn-group ms-auto me-2" role="group" aria-label="Gallery view toggle">
                <button
                  type="button"
                  class="btn btn-outline-secondary btn-sm"
                  :class="{ active: galleryView == 'single' }"
                  @click="showSingle"
                >
                  Single
                </button>

                <button
                  type="button"
                  class="btn btn-outline-secondary btn-sm"
                  :class="{ active: galleryView == 'grid' }"
                  @click="showGrid"
                >
                  Grid
                </button>
              </div>

              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>

            <div class="modal-body">
              <div v-if="galleryView == 'single'" class="gallery-single-wrap">
                <button class="gallery-nav gallery-nav-prev" type="button" @click="prevImage">
                  <i class="bi bi-chevron-left"></i>
                </button>

                <img
                  :src="galleryImages[activeGalleryIndex]"
                  class="gallery-full-img"
                  :alt="'Property image ' + (activeGalleryIndex + 1)"
                />

                <button class="gallery-nav gallery-nav-next" type="button" @click="nextImage">
                  <i class="bi bi-chevron-right"></i>
                </button>
              </div>

              <div v-if="galleryView == 'grid'" class="container-fluid">
                <div class="row g-3">
                  <div
                    class="col-6 col-md-4 col-lg-3"
                    v-for="(img, idx) in galleryImages"
                    :key="'thumb-' + idx"
                  >
                    <button class="thumb-btn" type="button" @click="thumbClick(idx)">
                      <img class="thumb-img" :src="img" :alt="'Thumbnail ' + (idx + 1)">
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div class="modal-footer gallery-thumbs" v-if="galleryView == 'single'">
              <button
                class="gallery-thumb-small"
                type="button"
                v-for="(img, idx) in galleryImages"
                :key="'small-thumb-' + idx"
                :class="{ active: activeGalleryIndex == idx }"
                @click="activeGalleryIndex = idx"
              >
                <img :src="img" :alt="'Thumbnail ' + (idx + 1)">
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="offcanvas offcanvas-end offcanvas-custom" tabindex="-1" id="mobileNav" aria-labelledby="mobileNavLabel">
        <div class="offcanvas-header">
          <h5 class="offcanvas-title text-white" id="mobileNavLabel">Menu</h5>
          <button type="button" class="btn-close btn-close-white" data-bs-dismiss="offcanvas" aria-label="Close"></button>
        </div>

        <div class="offcanvas-body">
          <div class="list-group list-group-flush">
            <a class="list-group-item list-group-item-action" href="/properties">Buy</a>
            <a class="list-group-item list-group-item-action" href="/sell">Sell</a>
            <a class="list-group-item list-group-item-action" href="/developments">Developments</a>
            <a class="list-group-item list-group-item-action" href="/commercial">Commercial</a>
          </div>
          <hr/>
        </div>
      </div>

      <Footer />
    </div>

    <div v-else>
      <div class="container py-5 text-center">
        <h1>Property not available</h1>
        <p>This property is either no longer active or is missing required regulatory information.</p>
        <a href="/properties" class="btn btn-all">Back to Properties</a>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import Menu from '@/components/Menu.vue'
import Footer from '@/components/Footer.vue'

export default {
  components: {
    Menu,
    Footer
  },

  data() {
    return {
      property: null,
      loading: true,
      currencyRates: null,
      agent: {},
      agent_2: {},
      agent_3: {},
      branch: {},
      contactRequested: false,
      contactRequested_2: false,
      contactRequested_3: false,
      whatsappLink: '',
      whatsappLink_2: '',
      whatsappLink_3: '',
      galleryView: 'single',
      activeGalleryIndex: 0,
      galleryModalInstance: null
    }
  },

  mounted() {
    this.fetchCurrencyRates()
    this.fetchListing()
    document.addEventListener('keydown', this.handleKeydown)
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeydown)
  },

  computed: {
    canDisplayProperty() {
      if (!this.property) {
        return false
      }

      if (this.property.status != 'Active') {
        return false
      }

      if (!this.hasRequiredRegulatoryInformation) {
        return false
      }

      return true
    },

    hasRequiredRegulatoryInformation() {
      if (!this.property) {
        return false
      }

      if (!this.hasValue(this.property.dldqrcodepath)) {
        return false
      }

      if (!this.hasValue(this.property.dldnopath)) {
        return false
      }

      if (!this.hasValue(this.agentLicense)) {
        return false
      }

      return true
    },

    hasConvertedPrice() {
      if (!this.property) {
        return false
      }

      if (!this.currencyRates) {
        return false
      }

      if (this.isTruthy(this.property.poa)) {
        return false
      }

      const price = parseInt(this.property.price, 10)

      if (isNaN(price)) {
        return false
      }

      if (price == 999999999) {
        return false
      }

      return true
    },

    displayPriceZAR() {
      if (!this.hasConvertedPrice) {
        return ''
      }

      const price = parseInt(this.property.price, 10)

      return new Intl.NumberFormat('en-ZA', {
        style: 'currency',
        currency: 'ZAR',
        maximumFractionDigits: 0
      }).format(price * this.currencyRates.aedToZar)
    },

    displayPriceUSD() {
      if (!this.hasConvertedPrice) {
        return ''
      }

      const price = parseInt(this.property.price, 10)

      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0
      }).format(price * this.currencyRates.aedToUsd)
    },

    agentsList() {
      const agents = []

      if (this.agent && this.agent.first_name) {
        agents.push({
          key: 'agent_1',
          agent: this.agent,
          whatsappLink: this.whatsappLink,
          contactRequested: this.contactRequested
        })
      }

      if (this.agent_2 && this.agent_2.first_name) {
        agents.push({
          key: 'agent_2',
          agent: this.agent_2,
          whatsappLink: this.whatsappLink_2,
          contactRequested: this.contactRequested_2
        })
      }

      if (this.agent_3 && this.agent_3.first_name) {
        agents.push({
          key: 'agent_3',
          agent: this.agent_3,
          whatsappLink: this.whatsappLink_3,
          contactRequested: this.contactRequested_3
        })
      }

      return agents
    },

    galleryImages() {
      if (!this.property) {
        return []
      }

      if (!Array.isArray(this.property.listing_images)) {
        return []
      }

      return this.property.listing_images
    },

    featuredImage() {
      if (this.galleryImages.length > 0) {
        return this.galleryImages[0]
      }

      return '/images/logos/ce-logo.png'
    },

    displayPrice() {
      if (!this.property) {
        return ''
      }

      if (this.isTruthy(this.property.poa)) {
        return 'POA'
      }

      if (parseInt(this.property.price, 10) == 999999999) {
        return 'POA'
      }

      return this.formatCurrency(this.property.price)
    },

    formattedDescription() {
      if (!this.property || !this.property.description) {
        return ''
      }

      return this.cleanText(this.property.description).replace(/\n/g, '<br>')
    },

    parkingCount() {
      const garages = this.numberValue(this.property.garages)
      const carports = this.numberValue(this.property.carports)
      const parking = this.numberValue(this.property.parking)

      return garages + carports + parking
    },

    extrasList() {
      if (!this.property || !this.property.extras) {
        return []
      }

      return this.cleanText(this.property.extras)
        .split('\n')
        .map((item) => {
          return item.trim()
        })
        .filter((item) => {
          return item != ''
        })
    },

    hasMap() {
      if (!this.property) {
        return false
      }

      if (this.property.map_x_position === null || this.property.map_y_position === null) {
        return false
      }

      if (this.property.map_x_position === undefined || this.property.map_y_position === undefined) {
        return false
      }

      return true
    },

    dldQrCode() {
      if (!this.property) {
        return ''
      }

      return this.property.dldqrcodepath || ''
    },

    dldPermitImage() {
      if (!this.property) {
        return ''
      }

      return this.property.dldnopath || ''
    },

    brokerLicense() {
      if (this.branch && this.hasValue(this.branch.branch_profile)) {
        return this.branch.branch_profile
      }

      return 'Pending'
    },

    agentLicense() {
      if (this.agent && this.agent.fidelity_fund_number) {
        return this.agent.fidelity_fund_number
      }

      if (this.agent_2 && this.agent_2.fidelity_fund_number) {
        return this.agent_2.fidelity_fund_number
      }

      if (this.agent_3 && this.agent_3.fidelity_fund_number) {
        return this.agent_3.fidelity_fund_number
      }

      return ''
    },

    listedAgo() {
      if (!this.property || !this.property.date_added) {
        return '—'
      }

      const added = new Date(this.property.date_added)
      const now = new Date()
      const diff = now.getTime() - added.getTime()
      const days = Math.floor(diff / (1000 * 60 * 60 * 24))

      if (days <= 0) {
        return 'Today'
      }

      if (days == 1) {
        return '1 day ago'
      }

      return days + ' days ago'
    }
  },

  methods: {
    async fetchCurrencyRates() {
      try {
        const response = await axios.get(
          'https://chas-dubai.stratitude-develop.co.za/api/fetch-currency-rates.php'
        )

        if (!response.data || response.data.success !== true) {
          return
        }

        this.currencyRates = {
          aedToZar: response.data.aedToZar,
          aedToUsd: response.data.aedToUsd,
          updatedAt: response.data.updatedAt,
          cachedAt: response.data.cachedAt
        }
      } catch (error) {
        console.log(error)
        this.currencyRates = null
      }
    },

    hasValue(value) {
      if (value === null || value === undefined) {
        return false
      }

      if (value.toString().trim() == '') {
        return false
      }

      if (value.toString().trim().toLowerCase() == 'pending') {
        return false
      }

      return true
    },

    cleanText(value) {
      if (!value) {
        return ''
      }

      return value
        .toString()
        .replace(/ΓÇö/g, '—')
        .replace(/ΓÇô/g, '–')
        .replace(/ΓÇÖ/g, '’')
        .replace(/ΓÇ£/g, '“')
        .replace(/ΓÇ¥/g, '”')
        .replace(/ΓÇó/g, '•')
        .replace(/Γ£ö∩╕Å/g, '✓')
        .replace(/≡ƒôì/g, '📍')
        .replace(/ΓÇª/g, '…')
        .replace(/┬á/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/\s+\n/g, '\n')
        .replace(/\n\s+/g, '\n')
    },

    async fetchListing() {
      this.loading = true

      try {
        const response = await axios.post(
          'https://chas-dubai.stratitude-develop.co.za/api/fetch-listing-residential.php',
          {
            id: this.$route.params.propertyId
          },
          {
            headers: {
              Accept: 'application/json'
            }
          }
        )

        this.property = this.extractListing(response.data)

        if (this.property) {
          await this.fetchAgents()
        }
      } catch (error) {
        console.log(error)
      }

      this.loading = false
    },

    extractListing(payload) {
      if (!payload) {
        return null
      }

      if (payload.data && payload.data.listing) {
        if (Array.isArray(payload.data.listing)) {
          return payload.data.listing[0] || null
        }

        if (payload.data.listing[0]) {
          return payload.data.listing[0]
        }

        if (payload.data.listing['0']) {
          return payload.data.listing['0']
        }
      }

      return null
    },

    async fetchAgents() {
      if (this.property.agent) {
        this.agent = await this.fetchAgent(this.property.agent)
      }

      if (this.property.agent_2) {
        this.agent_2 = await this.fetchAgent(this.property.agent_2)
      }

      if (this.property.agent_3) {
        this.agent_3 = await this.fetchAgent(this.property.agent_3)
      }

      await this.fetchBranchFromAgents()
      this.buildWhatsappLinks()
    },

    async fetchAgent(agentId) {
      try {
        const response = await axios.post(
          'https://chas-dubai.stratitude-develop.co.za/api/fetch-agent.php',
          {
            id: agentId
          },
          {
            headers: {
              Accept: 'application/json'
            }
          }
        )

        if (response.data && response.data.data && response.data.data.agent) {
          if (Array.isArray(response.data.data.agent)) {
            return response.data.data.agent[0] || {}
          }

          return response.data.data.agent
        }
      } catch (error) {
        console.log(error)
      }

      return {}
    },

    async fetchBranchFromAgents() {
      const branchId = this.getFirstBranchIdFromAgents()

      if (!branchId) {
        this.branch = {}
        return
      }

      this.branch = await this.fetchBranch(branchId)
    },

    getFirstBranchIdFromAgents() {
      const agents = [this.agent, this.agent_2, this.agent_3]

      for (let index = 0; index < agents.length; index += 1) {
        const agent = agents[index]

        if (agent && Array.isArray(agent.branches) && agent.branches.length > 0) {
          return agent.branches[0]
        }
      }

      return ''
    },

    async fetchBranch(branchId) {
      try {
        const response = await axios.post(
          'https://chas-dubai.stratitude-develop.co.za/api/fetch-branch.php',
          {
            id: branchId
          },
          {
            headers: {
              Accept: 'application/json'
            }
          }
        )

        if (response.data && response.data.data && response.data.data.branch) {
          if (Array.isArray(response.data.data.branch)) {
            return response.data.data.branch[0] || {}
          }

          return response.data.data.branch
        }
      } catch (error) {
        console.log(error)
      }

      return {}
    },

    buildWhatsappLinks() {
      this.whatsappLink = this.createWhatsappLink(this.agent)
      this.whatsappLink_2 = this.createWhatsappLink(this.agent_2)
      this.whatsappLink_3 = this.createWhatsappLink(this.agent_3)
    },

    createWhatsappLink(agent) {
      if (!agent || !agent.cell_number) {
        return ''
      }

      const currentURL = window.location.href
      const formattedNumber = this.normalisePhoneNumber(agent.cell_number)

      if (!formattedNumber) {
        return ''
      }

      const message = encodeURIComponent('Hello! I am interested in the property I found on your website: ' + currentURL)

      return 'https://wa.me/' + formattedNumber + '?text=' + message
    },

    normalisePhoneNumber(number) {
      if (!number) {
        return ''
      }

      let cleanedNumber = number.toString().replace(/\D/g, '')

      if (!cleanedNumber) {
        return ''
      }

      if (cleanedNumber.startsWith('00')) {
        cleanedNumber = cleanedNumber.substring(2)
      }

      if (cleanedNumber.startsWith('971')) {
        return cleanedNumber
      }

      if (cleanedNumber.startsWith('27')) {
        return cleanedNumber
      }

      if (cleanedNumber.charAt(0) == '0') {
        return '971' + cleanedNumber.substring(1)
      }

      if (cleanedNumber.length == 9 && cleanedNumber.charAt(0) == '5') {
        return '971' + cleanedNumber
      }

      return cleanedNumber
    },

    formatPhoneForLink(number) {
      return this.normalisePhoneNumber(number)
    },

    formatPhoneForDisplay(number) {
      const formattedNumber = this.normalisePhoneNumber(number)

      if (!formattedNumber) {
        return ''
      }

      if (formattedNumber.startsWith('971')) {
        const localNumber = formattedNumber.substring(3)

        if (localNumber.length == 9) {
          return '+971 ' + localNumber.substring(0, 2) + ' ' + localNumber.substring(2, 5) + ' ' + localNumber.substring(5)
        }

        return '+971 ' + localNumber
      }

      if (formattedNumber.startsWith('27')) {
        const localNumber = formattedNumber.substring(2)

        if (localNumber.length == 9) {
          return '+27 ' + localNumber.substring(0, 2) + ' ' + localNumber.substring(2, 5) + ' ' + localNumber.substring(5)
        }

        return '+27 ' + localNumber
      }

      return '+' + formattedNumber
    },

    requestAgentContact(key) {
      if (key == 'agent_1') {
        this.contactRequested = true
      }

      if (key == 'agent_2') {
        this.contactRequested_2 = true
      }

      if (key == 'agent_3') {
        this.contactRequested_3 = true
      }
    },

    openGallery(index) {
      this.galleryView = 'single'
      this.activeGalleryIndex = index

      this.$nextTick(() => {
        const modalEl = document.getElementById('galleryModal')

        if (modalEl && window.bootstrap) {
          this.galleryModalInstance = window.bootstrap.Modal.getOrCreateInstance(modalEl)
          this.galleryModalInstance.show()
        }
      })
    },

    showSingle() {
      this.galleryView = 'single'
    },

    showGrid() {
      this.galleryView = 'grid'
    },

    thumbClick(index) {
      this.activeGalleryIndex = index
      this.galleryView = 'single'
    },

    nextImage() {
      if (this.galleryImages.length == 0) {
        return
      }

      if (this.activeGalleryIndex >= this.galleryImages.length - 1) {
        this.activeGalleryIndex = 0
      } else {
        this.activeGalleryIndex += 1
      }
    },

    prevImage() {
      if (this.galleryImages.length == 0) {
        return
      }

      if (this.activeGalleryIndex <= 0) {
        this.activeGalleryIndex = this.galleryImages.length - 1
      } else {
        this.activeGalleryIndex -= 1
      }
    },

    handleKeydown(event) {
      const modalEl = document.getElementById('galleryModal')

      if (!modalEl) {
        return
      }

      if (!modalEl.classList.contains('show')) {
        return
      }

      if (event.key == 'ArrowRight') {
        this.nextImage()
      }

      if (event.key == 'ArrowLeft') {
        this.prevImage()
      }

      if (event.key == 'Escape' && this.galleryModalInstance) {
        this.galleryModalInstance.hide()
      }
    },

    printToPDF() {
      window.print()
    },

    formatCurrency(value) {
      const amount = parseInt(value, 10)

      if (isNaN(amount)) {
        return 'AED 0'
      }

      return new Intl.NumberFormat('en-AE', {
        style: 'currency',
        currency: 'AED',
        maximumFractionDigits: 0
      }).format(amount)
    },

    formatSize(value, unit) {
      const number = this.numberValue(value)

      if (number == 0) {
        return '—'
      }

      const cleanNumber = new Intl.NumberFormat('en-AE', {
        maximumFractionDigits: 0
      }).format(number)

      if (unit == 'Square Feet') {
        return cleanNumber + ' sqft'
      }

      if (unit == 'Square Metres' || unit == 'Square Meters') {
        return cleanNumber + ' sqm'
      }

      if (unit) {
        return cleanNumber + ' ' + unit
      }

      return cleanNumber
    },

    formatDate(value) {
      if (!value) {
        return '—'
      }

      const date = new Date(value)

      if (isNaN(date.getTime())) {
        return value
      }

      return date.toLocaleDateString('en-AE', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      })
    },

    showNumber(value) {
      return this.numberValue(value) > 0
    },

    numberValue(value) {
      const number = parseFloat(value)

      if (isNaN(number)) {
        return 0
      }

      return number
    },

    isTruthy(value) {
      if (value === true) {
        return true
      }

      if (value == 'true') {
        return true
      }

      if (value == 1) {
        return true
      }

      return false
    }
  }
}
</script>

<style scoped>
.property-hero {
  position: relative;
  min-height: 600px;
  overflow: hidden;
  background: #111;
}

.hero-image-button {
  position: absolute;
  inset: 0;
  border: 0;
  padding: 0;
  width: 100%;
  height: 100%;
  background: transparent;
  cursor: zoom-in;
}

.hero-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.hero-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(0,0,0,.65), rgba(0,0,0,.1));
  pointer-events: none;
}

.hero-content {
  position: relative;
  z-index: 2;
  color: #fff;
}

.property-kicker {
  display: inline-block;
  background: #255699;
  color: #fff;
  padding: 7px 16px;
  font-weight: 700;
  font-size: 13px;
  text-transform: uppercase;
}

.property-title {
  font-size: clamp(30px, 5vw, 64px);
  line-height: 1;
}

.property-sub {
  font-size: 18px;
  font-weight: 600;
}

.property-summary {
  background: #fff;
  border-bottom: 1px solid #eee;
  padding: 18px 0;
}

.summary-chip {
  background: #f7f7f7;
  padding: 10px 18px;
  font-size: 14px;
  color: #000;
}

.btn-gallery,
.btn-all {
  background: #255699;
  color: #fff;
  border: 1px solid #255699;
}

.btn-gallery:hover,
.btn-all:hover {
  background: #1c4379;
  color: #fff;
}

.btn-outline-brand {
  border: 1px solid #255699;
  color: #255699;
  background: #fff;
}

.btn-outline-brand:hover {
  background: #255699;
  color: #fff;
}

.section-title {
  font-size: 24px;
  font-weight: 800;
  color: #333;
}

.section-rule {
  width: 70px;
  height: 4px;
  background: #255699;
  margin: 12px 0 24px;
}

.paragraph {
  color: #444;
  font-size: 16px;
  line-height: 1.8;
}

.property-box,
.agent-list-card {
  background: #f7f7f7;
  border-radius: 18px;
  padding: 24px;
}

.ref-label {
  color: #666;
  font-size: 13px;
  font-weight: 800;
  text-transform: uppercase;
  margin-bottom: 6px;
}

.property-price {
  font-size: 30px;
  font-weight: 800;
  color: #255699;
}

.converted-property-price {
  font-size: 16px;
  font-weight: 500;
  opacity: 0.75;
  line-height: 1.4;
}

.feature-item {
  background: #f7f7f7;
  border-radius: 12px;
  padding: 14px 16px;
  color: #333;
}

.regulatory-panel {
  background: #f7f7f7;
  border-radius: 18px;
  padding: 32px;
}

.regulatory-title {
  font-size: 26px;
  font-weight: 800;
  margin-bottom: 24px;
  color: #333;
}

.reg-row {
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: 18px;
  margin-bottom: 18px;
  align-items: center;
  color: #444;
}

.reg-row strong {
  font-size: 16px;
  font-weight: 800;
}

.reg-row span {
  font-size: 16px;
}

.dld-qr {
  width: 190px;
  max-width: 100%;
  height: auto;
}

.dld-label {
  font-size: 20px;
  color: #444;
}

.dld-permit-pill {
  display: inline-block;
  margin-top: 10px;
  background: #e2e2e2;
  border-radius: 14px;
  padding: 12px 28px;
  font-size: 18px;
  color: #444;
  min-width: 180px;
}

.agent-list-item {
  display: flex;
  gap: 16px;
  padding: 18px 0;
  border-bottom: 1px solid #ddd;
}

.agent-list-item:last-child {
  border-bottom: 0;
  padding-bottom: 0;
}

.agent-list-photo {
  width: 96px;
  height: 96px;
  border-radius: 50%;
  overflow: hidden;
  background: #e8e8e8;
  flex: 0 0 96px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.agent-list-photo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.agent-list-photo-placeholder {
  font-size: 42px;
  color: #777;
}

.agent-list-content {
  flex: 1;
  min-width: 0;
}

.agent-name {
  font-size: 18px;
  font-weight: 800;
  margin-bottom: 2px;
  color: #333;
}

.agent-role {
  margin-bottom: 8px;
  color: #666;
}

.agent-contact-list {
  display: flex;
  flex-direction: column;
  gap: 7px;
  margin-top: 10px;
}

.agent-contact-list a {
  color: #255699;
  text-decoration: none;
  font-weight: 700;
  overflow-wrap: anywhere;
}

.agent-actions .btn-sm {
  padding: 7px 18px;
  font-size: 13px;
}

.floating-whatsapp {
  position: fixed;
  top: 50%;
  right: 20px;
  transform: translateY(-50%);
  z-index: 999;
  padding: 10px 14px;
  background-color: #efefef;
  border-radius: 8px;
}

.gallery-modal {
  background: #111;
  color: #fff;
}

.gallery-modal .modal-header,
.gallery-modal .modal-footer {
  border-color: rgba(255,255,255,.15);
}

.gallery-modal .btn-close {
  filter: invert(1);
}

.gallery-single-wrap {
  position: relative;
  min-height: calc(100vh - 180px);
  display: flex;
  align-items: center;
  justify-content: center;
}

.gallery-full-img {
  max-width: 100%;
  max-height: calc(100vh - 220px);
  object-fit: contain;
}

.gallery-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 5;
  border: 0;
  width: 54px;
  height: 54px;
  border-radius: 50%;
  background: rgba(255,255,255,.2);
  color: #fff;
  font-size: 28px;
}

.gallery-nav-prev {
  left: 20px;
}

.gallery-nav-next {
  right: 20px;
}

.thumb-btn {
  display: block;
  width: 100%;
  border: 0;
  padding: 0;
  background: transparent;
  cursor: zoom-in;
}

.thumb-img {
  width: 100%;
  height: 220px;
  object-fit: cover;
  border-radius: 10px;
}

.gallery-thumbs {
  overflow-x: auto;
  flex-wrap: nowrap;
  justify-content: flex-start;
}

.gallery-thumb-small {
  border: 2px solid transparent;
  padding: 0;
  background: transparent;
  width: 90px;
  height: 60px;
  flex: 0 0 auto;
  opacity: .65;
}

.gallery-thumb-small.active {
  border-color: #fff;
  opacity: 1;
}

.gallery-thumb-small img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

@media (max-width: 991px) {
  .property-hero {
    min-height: 420px;
  }

  .reg-row {
    grid-template-columns: 1fr;
    gap: 4px;
  }

  .floating-whatsapp {
    display: none;
  }
}

@media (max-width: 575px) {
  .agent-list-item {
    align-items: flex-start;
  }

  .agent-list-photo {
    width: 76px;
    height: 76px;
    flex-basis: 76px;
  }
}
.gallery-modal {
  background: #111;
  color: #fff;
  height: 100vh;
}

.gallery-modal .modal-body {
  overflow-y: auto;
  min-height: 0;
}

.gallery-modal .modal-content {
  height: 100vh;
}
</style>